import { useTranslation } from "react-i18next";
import { useState } from "react";
import { testIds } from "@/testIds";
import type { ThemeDescriptor, ThemeId } from "@/lib/api";
import { isSelectable, themeAllowed, unavailableReason } from "@/lib/themes/look";
import styles from "./MobileReader.module.scss";

const M = testIds.mobile;

export interface MobileLookSheetProps {
  themes: readonly ThemeDescriptor[];
  /** The theme the document prints with now. */
  current: ThemeId;
  /** The class sets its own look (e.g. IEEEtran): why, and no choice. */
  locked?: string | null;
  /** The themes this class allows; none means any. */
  allowed?: readonly ThemeId[] | null;
  canEdit: boolean;
  /** Show a theme on the page behind the sheet, unsaved. */
  onPreview: (id: ThemeId) => void;
  /** Save the previewed theme (the PDF rebuilds with it). */
  onApply: (id: ThemeId) => Promise<void>;
  /** Put the saved theme back on the page and close. */
  onCancel: () => void;
}

/**
 * Look on a phone (Olivia, 10 Oct): a half sheet of theme cards with the page
 * live behind it. A tap previews the theme in Read; Apply saves it and the PDF
 * rebuilds; Cancel puts the saved one back. A reader who can't edit sees the
 * look's name only.
 */
export function MobileLookSheet({ themes, current, locked, allowed, canEdit, onPreview, onApply, onCancel }: MobileLookSheetProps) {
  const [picked, setPicked] = useState<ThemeId>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t: tr } = useTranslation("mobile");
  const name = (id: ThemeId) => themes.find((t) => t.id === id)?.name ?? (id === "classic" ? tr("look.classic") : id);

  if (!canEdit || locked) {
    return (
      <div className={styles.lookBody} data-testid={M.lookSheet}>
        <p className={styles.lookNote}>
          {locked ? locked : tr("look.readOnly", { name: name(current) })}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.lookBody} data-testid={M.lookSheet}>
      <div className={styles.lookCards} role="radiogroup" aria-label={tr("look.label")}>
        {themes.filter((t) => themeAllowed(t.id, allowed)).map((t) => {
          const why = unavailableReason(t);
          const on = picked === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!!why || !isSelectable(t)}
              className={styles.lookCard}
              data-testid={M.lookCard}
              data-theme={t.id}
              onClick={() => {
                setPicked(t.id);
                onPreview(t.id);
              }}
            >
              <span className={styles.lookSwatch} style={{ background: t.colours.paper, color: t.colours.heading, fontFamily: t.fonts.headingCss }} aria-hidden="true">
                Aa
                <span className={styles.lookAccent} style={{ background: t.colours.accent }} />
              </span>
              <span className={styles.lookName}>{t.name}</span>
              <span className={styles.muted}>{why ?? t.for}</span>
            </button>
          );
        })}
      </div>
      {error && <p className={styles.shareError} role="alert">{error}</p>}
      <div className={styles.pair}>
        <button type="button" className={styles.secondary} data-testid={M.lookCancel} disabled={saving} onClick={onCancel}>
          {tr("look.cancel")}
        </button>
        <button
          type="button"
          className={styles.primary}
          data-testid={M.lookApply}
          disabled={saving || picked === current}
          onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              await onApply(picked);
            } catch (e) {
              setError(e instanceof Error && e.message ? e.message : tr("look.failed"));
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? tr("look.applying") : tr("look.apply")}
        </button>
      </div>
    </div>
  );
}
